import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { useSlackCRUDActions } from '@mm/state/actions/SlackCRUDActions';
import { selectSlackAccounts } from '@mm/state/selectors/slackSelectors';
import { landmineNames } from '@mm/constants/landmine-names';
import { SlackHeaderComponent } from './SlackHeaderComponent';
import { ReactComponent as SlackIcon } from 'assets/slack.svg';
import { ReactComponent as AlarmIcon } from '../../../icons/alarm-bell.svg';
import { BaseIntegrationHeader } from '../../../BaseIntegration/BaseIntegrationHeader';
import { BaseIntegration } from '../../../BaseIntegration/BaseIntegration';
import { useConnectSlack } from './use-connect-slack';
import { IntegrationSectionProps } from '../../../IntegrationsPanel/IntegrationsPanel';
import { EditOutlined } from '@ant-design/icons';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { SlackAccount } from '@mm/state/model/SlackModel';
import { selectSlackConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { useFetchConnections } from '../../../use-fetch-connections';
import { useViewActions } from '@mm/state/actions/viewActions';
import ConnectionModel, {
  FormattedConnectionModel,
} from '@mm/state/model/ConnectionModel';
import {
  ComponentDataItem,
  DefaultCell,
} from '../../../../../../contacts/components/ContactsTable/ContactTable';
import {
  Grid,
  GridCell,
  GridLabels,
  GridRow,
} from '../../../../../../../../common/components/Grid/Grid';
import { TableLabel } from '../../../../../../../../common/components/TableGrid/TableGrid';
import { PrimaryButton } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { ThreeDotMenu } from '../../../../../../../../common/components/ThreeDotMenu/ThreeDotMenu';
import { selectAudienceSorted } from '@mm/state/selectors/messengerAdSelectors';
import store from '@mm/state';
import AudiencesModel from '@mm/state/model/AudiencesModel';
import { BotBuilderRoutes } from '../../../../../../../../routes/bot-builder-routes';
import { Link } from 'react-router-dom';
import moment from 'moment';

type CellProps = { item: ConnectionModel };

const renderChannelNameCell = (item: ConnectionModel) => (
  <GridCell>{item.data && item.data.listName}</GridCell>
);

const renderAudienceCell = (item?: AudiencesModel) => {
  if (!item) {
    return <GridCell>No audience(Please select an audience)</GridCell>;
  }

  const chatbotId = activeChatbotIdSelector(store.getState());

  return (
    <GridCell>
      {item ? (
        <Link
          key={item?.id}
          to={BotBuilderRoutes.audiences.getLink({
            chatbotId,
            audienceId: item?.id,
          })}
        >
          {item?.name}
        </Link>
      ) : (
        <span>-</span>
      )}
    </GridCell>
  );
};

const renderDataMessageCell =
  (field: string) =>
  ({ item }: CellProps) =>
    (
      <GridCell className="contacts-table__grid-cell">
        {item.data.messageInfo?.[field] || ''}
      </GridCell>
    );

const renderCreatedCell = (item: ConnectionModel) => (
  <GridCell className="contacts-table__grid-cell">
    {moment(item.createdAt).format('MM/DD/YYYY')}
  </GridCell>
);

const renderActionsCell = (
  item: ConnectionModel,
  editSheet: (id: number) => void,
  deleteGoogleSheet: (item: ConnectionModel) => void,
) => {
  const onSelectItem = (menuItem: string) => {
    switch (menuItem) {
      case 'delete':
        return deleteGoogleSheet(item);
      default:
        break;
    }
  };

  const elements = [{ name: 'Delete', value: 'delete' }];

  const onMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <GridCell className="action-cell">
      <EditOutlined
        onClick={() => editSheet(item.id)}
        className="integrations__edit-icon"
      />
      <ThreeDotMenu
        className="integrations__three-dot-menu"
        elements={elements}
        selectItem={onSelectItem}
        onClick={onMenuClick}
      />
    </GridCell>
  );
};

const generateTableCells = (
  updateConnection: (id: number) => void,
  deleteConnection: (item: FormattedConnectionModel) => void,
  audiences: AudiencesModel[],
) => {
  const cells: ComponentDataItem[] = [
    {
      label: 'Channel',
      keyName: 'channel',
      Cell: ({ item }: CellProps) => renderChannelNameCell(item),
    },
    {
      label: 'Customers.ai Audience',
      keyName: 'audience',
      Cell: ({ item }: CellProps) =>
        renderAudienceCell(
          audiences.find(audience => item.audienceId === audience.id),
        ),
    },
    {
      label: 'Send as bot',
      keyName: 'sendAsBot',
      Cell: renderDataMessageCell('sendAsBot'),
    },
    {
      label: 'Created',
      keyName: 'created',
      Cell: ({ item }: CellProps) => renderCreatedCell(item),
    },
    {
      label: 'Actions',
      keyName: 'actions',
      Cell: ({ item }: CellProps) =>
        renderActionsCell(item, updateConnection, deleteConnection),
    },
  ];
  return cells;
};

export const SlackIntegrations = ({ section }: IntegrationSectionProps) => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const accounts = useSelector(selectSlackAccounts);
  const connections = useSelector(selectSlackConnections);
  const filteredConnections = connections.filter(
    connection => !!connection.slackAccessToken,
  );
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const audiences = useSelector(selectAudienceSorted);

  const { fetch } = useFetchConnections();

  const { getAccounts, disconnectAccount } = useSlackCRUDActions();
  const { openGeneralConfirmModal } = useModalActions();
  const { connectSlack } = useConnectSlack();
  const { create, deleteConnection } = useConnectionCRUDActions();
  const { showSlideoutMenu } = useViewActions();
  const { openDeleteConfirmModal } = useModalActions();

  const gridTemplateColumns = `25% 30% 20% 15% 10%`;

  useEffect(() => {
    getAccounts(facebookPageId);
    fetch(1, 200, 'slack');
  }, []);

  const onDisconnect = (accountId: number) => {
    const teamName = accounts?.find(acc => acc.id === accountId)?.teamName;
    openGeneralConfirmModal({
      headerText: `Are you sure you want to disconnect ${teamName} account from Slack Notifications?`,
      messageText: '',
      onCloseHandler: ok => {
        if (ok) {
          disconnectAccount(accountId, facebookPageId);
        }
      },
      okButtonText: 'Disconnect',
      okButtonStyling: { red: true },
      cancelButtonText: 'Cancel',
      cancelButtonFirst: true,
      css: {
        primaryButton: 'integrations__diconnect-modal-primary-button',
        secondaryButton: 'integrations__confirm-modal-secondary-button',
      },
    });
  };

  const openSlackSlideoutMenu = (id: number) => {
    showSlideoutMenu('slackIntegrationEditor', {
      widgetId: id,
    });
  };

  const addNewConnection = async (slackAccount?: SlackAccount) => {
    const connection = await create(facebookPageId, {
      name: 'Slack Connection',
      connectionKind: 'slack',
      slackParams: { accessTokenId: slackAccount?.id || accounts[0].id },
    });

    if (connection.id) {
      openSlackSlideoutMenu(connection.id);
    }
  };

  const headerComponent = (
    <SlackHeaderComponent
      accountsCount={accounts.length}
      connectionsCount={filteredConnections.length}
      addNewConnection={addNewConnection}
    />
  );

  const sectionProps = {
    landmineName: section
      ? landmineNames?.[section?.landmineName]
      : 'SLACK_INTEGRATIONS',
    featureFlag: section?.requiredFeature,
  };

  const onClickDelete = (connection: ConnectionModel) => {
    const deleteCallback = async () => {
      if (connection) {
        setTimeout(async () => {
          await deleteConnection(activeFbPageId, connection);
        }, 400);
      }
    };

    openDeleteConfirmModal({
      headerText: 'Are you sure you want to delete this connection?',
      newStyle: true,
      deleteCallback,
    });
  };

  const cellData = generateTableCells(
    openSlackSlideoutMenu,
    onClickDelete,
    audiences,
  );

  return (
    <BaseIntegration
      titleComponent={
        <BaseIntegrationHeader
          title="Slack Notifications"
          subtitle="Track customers activity with Slack"
          icon={<SlackIcon />}
          headerRightComponent={headerComponent}
          css={{ icon: 'integrations__slack-header-icon' }}
          {...sectionProps}
        />
      }
      emptyStateConfig={{
        text: 'Never miss your leads with notifications on Slack',
        button: 'Add a Slack Account',
        onClick: connectSlack,
        icon: <AlarmIcon />,
        ...sectionProps,
      }}
      showEmptyState={!accounts.length}
    >
      {accounts.map(account => (
        <Grid
          className="integrations-grid"
          key={`slack-connection-${account.id}`}
        >
          <div className="integrations-grid__account-header">
            <div className="integrations-grid__account-info">
              Slack Account:{' '}
              <span className="integrations-grid___account-name">
                {account.teamName}
              </span>
            </div>
            {!account.readOnly && (
              <PrimaryButton
                className="integrations__header-add-button-secondary"
                onClick={() => onDisconnect(account.id)}
                color="secondary-blue"
              >
                Disconnect
              </PrimaryButton>
            )}
          </div>
          {filteredConnections.find(
            connection => connection.slackAccessToken?.id === account.id,
          ) && (
            <>
              <GridLabels
                className="integrations-grid__labels"
                gridTemplateColumns={gridTemplateColumns}
              >
                {cellData.map(cell => (
                  <TableLabel
                    key={(cell.keyName as string) || cell.label?.toString()}
                    dataItem={cell}
                    orderByAtTheEnd
                  />
                ))}
              </GridLabels>

              <div className="integrations-grid__rows-container">
                {filteredConnections
                  .filter(
                    connection =>
                      connection.slackAccessToken?.id === account.id,
                  )
                  .map(item => (
                    <GridRow
                      key={item.id}
                      gridTemplateColumns={gridTemplateColumns}
                    >
                      {cellData.map(({ Cell, keyName }) =>
                        Cell ? (
                          <Cell key={keyName} item={item} />
                        ) : (
                          <DefaultCell
                            keyName={keyName as string}
                            item={item}
                          />
                        ),
                      )}
                    </GridRow>
                  ))}
              </div>
            </>
          )}
        </Grid>
      ))}
    </BaseIntegration>
  );
};
